import { ThemeEvents } from '@theme/events';

/**
 * TK: PDP doprava — dva custom elementy v buy boxu.
 *
 * <tk-pdp-fsb>            jezdec „doprava zdarma", realtime na cart:update
 * <tk-shipping-options>   hover/tap panel s tabulkou dopravců
 *
 * Server render (Liquid) drží správný stav při prvním paintu i po variant
 * morphi — JS jen dopočítává změny košíku, které nezpůsobí re-render stránky.
 */

/** Nezlomitelná mezera — money-tk ji používá jako oddělovač tisíců i před měnou ( ). */
const NBSP = ' ';

/**
 * Formátuje částku stejně jako snippets/money-tk.liquid.
 * @param {number} minor - částka v minoritních jednotkách (haléře/centy)
 * @param {string} currency - ISO kód měny košíku
 * @returns {string}
 */
function formatMoney(minor, currency) {
  const value = minor / 100;

  if (currency === 'CZK') {
    const rounded = Math.round(value);
    const grouped = String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    return `${grouped}${NBSP}Kč`;
  }

  if (currency === 'EUR') {
    const [whole, cents] = value.toFixed(2).split('.');
    const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    return `${grouped},${cents}${NBSP}€`;
  }

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'cs', {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** Košík se během jedné akce (add + drawer render) ozve víckrát — jedna fetch stačí. */
let cartTotalPromise = null;

/**
 * @returns {Promise<{total: number, variantIds: number[]}|null>} aktuální
 *   cart.total_price v minoritních jednotkách + varianty v košíku (bez dárkového řádku)
 */
function fetchCartTotal() {
  if (cartTotalPromise) return cartTotalPromise;

  const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';

  cartTotalPromise = fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) =>
      cart && typeof cart.total_price === 'number'
        ? {
            total: cart.total_price,
            variantIds: (cart.items || [])
              .filter((item) => !(item.properties && item.properties._darek === 'ano'))
              .map((item) => item.variant_id),
          }
        : null
    )
    .catch(() => null)
    .finally(() => {
      // krátké okno, ve kterém se paralelní cart:update eventy svezou s toutéž fetch
      setTimeout(() => {
        cartTotalPromise = null;
      }, 150);
    });

  return cartTotalPromise;
}

const STATE_CLASSES = [
  'tk-pdp-fsb--done',
  'tk-pdp-fsb--reach',
  'tk-pdp-fsb--product',
  'tk-pdp-fsb--progress',
  'tk-pdp-fsb--hidden',
];

class TkPdpFreeShippingBar extends HTMLElement {
  static observedAttributes = ['data-cart-total', 'data-product-price', 'data-threshold', 'data-in-cart'];

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#render();
  }

  /**
   * Návrat tlačítkem zpět (bfcache) obnoví HTML s košíkem, jaký byl při opuštění
   * stránky — mezitím se mohl změnit (drawer, jiná záložka). Přepočítej z /cart.js.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) this.#onCartUpdate();
  };

  #onCartUpdate = async () => {
    const cart = await fetchCartTotal();
    if (cart == null || !this.isConnected) return;

    // setAttribute → attributeChangedCallback → #render (jen když se hodnota liší)
    const inCart = cart.variantIds.includes(Number(this.dataset.variantId)) ? '1' : '0';
    if (this.dataset.inCart !== inCart) {
      this.dataset.inCart = inCart;
    }
    if (this.dataset.cartTotal !== String(cart.total)) {
      this.dataset.cartTotal = String(cart.total);
    }
  };

  #render() {
    const threshold = Number(this.dataset.threshold) || 0;
    if (threshold <= 0) return;

    const cartTotal = Math.max(0, Number(this.dataset.cartTotal) || 0);
    // Varianta už v košíku je → nepřičítat ji podruhé (viz tk-free-shipping.liquid)
    const price = this.dataset.inCart === '1' ? 0 : Math.max(0, Number(this.dataset.productPrice) || 0);
    const currency = this.dataset.currency || 'CZK';
    const prospective = cartTotal + price;

    // Prázdný košík + produkt pod hranicí → nic (viz komentář v tk-free-shipping.liquid)
    let state = cartTotal > 0 ? 'progress' : 'hidden';
    if (cartTotal >= threshold) state = 'done';
    else if (prospective >= threshold) state = cartTotal > 0 ? 'reach' : 'product';

    const cartPct = Math.min(100, (cartTotal / threshold) * 100);
    const prospPct = Math.min(100, (prospective / threshold) * 100);

    this.style.setProperty('--tk-fsb-cart', `${cartPct.toFixed(1)}%`);
    this.style.setProperty('--tk-fsb-prosp', `${prospPct.toFixed(1)}%`);

    this.classList.remove(...STATE_CLASSES);
    this.classList.add(`tk-pdp-fsb--${state}`);

    const text = this.querySelector('[data-tk-fsb-text]');
    if (!text || state === 'hidden') return;

    const template = {
      done: this.dataset.tplDone,
      reach: this.dataset.tplReach,
      product: this.dataset.tplProduct,
      progress: this.dataset.tplRemaining,
    }[state];

    if (!template) return;

    const amount = `<span class="tk-pdp-fsb__amount">${formatMoney(threshold - prospective, currency)}</span>`;
    const html = template.replace('%%AMOUNT%%', amount);

    if (text.innerHTML !== html) text.innerHTML = html;
  }
}

/** Mezera mezi kurzorem/triggerem a panelem. */
const PANEL_GAP = 12;
/** Odstup panelu od okraje viewportu. */
const PANEL_MARGIN = 8;
/** Panel se nezavře hned — kurzor musí stihnout přejet mezeru dovnitř. */
const CLOSE_DELAY = 160;

/**
 * Spodní hrana přilepené hlavičky (header + announcement bar) v souřadnicích viewportu.
 *
 * Odscrollovaná stránka má header `position: sticky` u horního okraje, takže panel
 * kotvený ke kurzoru nebo nad trigger by skončil pod ním. Bereme jen sekce, které
 * jsou reálně sticky/fixed — staticky posazená hlavička odscrolluje pryč a překážet
 * nemůže. `getBoundingClientRect` počítá i s transformem, takže schovaný scroll-up
 * header vyjde v záporu a `Math.max(0, …)` ho vyřadí.
 *
 * @returns {number} px od horního okraje viewportu (0 = žádná hlavička nepřekáží)
 */
function stickyHeaderBottom() {
  let bottom = 0;

  for (const section of document.querySelectorAll('#header-group > .shopify-section')) {
    const position = getComputedStyle(section).position;
    if (position !== 'sticky' && position !== 'fixed') continue;
    bottom = Math.max(bottom, section.getBoundingClientRect().bottom);
  }

  return Math.max(0, bottom);
}

/**
 * Plovoucí panel s tabulkou dopravy.
 *
 * Panel je `position: fixed` a pozicuje ho JS: na myši **nalevo od kurzoru**
 * (když se vlevo nevejde, spadne zpět nad/pod trigger), na dotyku a klávesnici
 * vždy nad/pod trigger. Fixed pozice je nutná — absolutní panel v buy boxu
 * mizel pod kartou „Doporučujeme zakoupit".
 */
class TkShippingOptions extends HTMLElement {
  #trigger = null;
  #panel = null;
  /** @type {{x: number, y: number} | null} kotva kurzoru; null = kotvi k triggeru */
  #anchor = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  get #isHoverDevice() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  connectedCallback() {
    this.#trigger = this.querySelector('.tk-shipopt__trigger');
    this.#panel = this.querySelector('.tk-shipopt__panel');
    if (!this.#trigger || !this.#panel) return;

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.#trigger.addEventListener('click', this.#onClick);
    this.#trigger.addEventListener('focus', this.#onFocus);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onKeydown);
    window.addEventListener('scroll', this.#reposition, { passive: true });
    window.addEventListener('resize', this.#reposition);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.#trigger?.removeEventListener('click', this.#onClick);
    this.#trigger?.removeEventListener('focus', this.#onFocus);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('scroll', this.#reposition);
    window.removeEventListener('resize', this.#reposition);
    this.#clearCloseTimer();
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#clearCloseTimer();
    // Přejezd mezery trigger→panel vyvolá leave+enter znovu. Když už je panel
    // otevřený, NEPŘEPOZICOVÁVEJ ho — jinak by kurzoru poskočil pod rukama.
    if (this.hasAttribute('data-open')) return;
    this.#anchor = { x: event.clientX, y: event.clientY };
    this.#setOpen(true);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    // odklad: mezi triggerem a panelem je mezera, přes kterou kurzor přejíždí
    this.#clearCloseTimer();
    this.#closeTimer = setTimeout(() => this.#setOpen(false), CLOSE_DELAY);
  };

  #onClick = () => {
    // na myši řídí panel hover; klik by ho jen probliknul
    if (this.#isHoverDevice) return;
    this.#anchor = null;
    this.#setOpen(!this.hasAttribute('data-open'));
  };

  #onFocus = () => {
    this.#clearCloseTimer();
    this.#anchor = null;
    this.#setOpen(true);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#setOpen(false);
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.hasAttribute('data-open')) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#setOpen(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
    this.#setOpen(false);
    this.#trigger?.focus();
  };

  #reposition = () => {
    if (this.hasAttribute('data-open')) this.#position();
  };

  #clearCloseTimer() {
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  }

  /** @param {boolean} open */
  #setOpen(open) {
    if (open) this.#position();
    this.toggleAttribute('data-open', open);
    this.#trigger?.setAttribute('aria-expanded', String(open));
  }

  /** Spočítá a nastaví pozici panelu. Měří se i skrytý (visibility: hidden má layout). */
  #position() {
    const panel = this.#panel;
    const trigger = this.#trigger;
    if (!panel || !trigger) return;

    const { width, height } = panel.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - PANEL_MARGIN;
    // horní mez není okraj viewportu, ale spodek přilepené hlavičky
    const minTop = Math.max(PANEL_MARGIN, stickyHeaderBottom() + PANEL_MARGIN);
    const maxTop = window.innerHeight - height - PANEL_MARGIN;

    let left;
    let top;

    const leftOfCursor = this.#anchor ? this.#anchor.x - width - PANEL_GAP : -1;

    if (this.#anchor && leftOfCursor >= PANEL_MARGIN) {
      // nalevo od kurzoru, svisle vycentrovaný na něj
      left = leftOfCursor;
      top = this.#anchor.y - height / 2;
    } else {
      // fallback (dotyk, klávesnice, úzké okno): pod trigger, jinak nad něj
      const rect = trigger.getBoundingClientRect();
      left = rect.left;
      top = rect.bottom + PANEL_GAP;
      // nad trigger jen když se tam vejde POD hlavičku — jinak by ho clamp
      // stáhl zpátky dolů a panel by trigger překryl
      const above = rect.top - height - PANEL_GAP;
      if (top > maxTop && above >= minTop) top = above;
    }

    panel.style.left = `${Math.min(Math.max(PANEL_MARGIN, left), Math.max(PANEL_MARGIN, maxLeft))}px`;
    panel.style.top = `${Math.min(Math.max(minTop, top), Math.max(minTop, maxTop))}px`;
  }
}

if (!customElements.get('tk-pdp-fsb')) {
  customElements.define('tk-pdp-fsb', TkPdpFreeShippingBar);
}

if (!customElements.get('tk-shipping-options')) {
  customElements.define('tk-shipping-options', TkShippingOptions);
}
